import type { Dispatch } from 'react';
import { CalendarClock, Loader2, TriangleAlert } from 'lucide-react';
import {
  getImportCounts,
  getLaunchRange,
  type SubmitFailureReason,
  type WizardAction,
  type WizardState,
} from '@/components/campaign-import/import-wizard.reducer';
import { Button } from '@/components/ui/button';
import { useI18nContext } from '@/i18n/i18n-react';
import { formatShortDateTime } from '@/lib/utils';

/** `schedule` imports and schedules the batch; `draft` only imports it. */
export type SubmitMode = 'schedule' | 'draft';

export interface ConfirmStepProps {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  onSubmit: (mode: SubmitMode) => void;
}

function useFailureMessages(): Record<SubmitFailureReason, string> {
  const { LL } = useI18nContext();
  return {
    network: LL.campaignImport.confirm.failures.network(),
    server: LL.campaignImport.confirm.failures.server(),
    conflict: LL.campaignImport.confirm.failures.conflict(),
  };
}

export function ConfirmStep({ state, dispatch, onSubmit }: ConfirmStepProps) {
  const { LL, locale } = useI18nContext();
  const failures = useFailureMessages();
  const counts = getImportCounts(state);
  const { submission } = state;
  const isSending = submission.status === 'sending';
  const range = getLaunchRange(state);
  const from = range ? formatShortDateTime(range.earliest, locale) : '';
  const to = range ? formatShortDateTime(range.latest, locale) : '';

  return (
    <div className="space-y-6">
      <dl className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3">
          <dt className="text-xs text-white/60">
            {LL.campaignImport.confirm.fileLabel()}
          </dt>
          <dd className="truncate font-semibold">{state.file?.fileName}</dd>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3">
          <dt className="text-xs text-white/60">
            {LL.campaignImport.confirm.toCreateLabel()}
          </dt>
          <dd className="text-2xl font-semibold text-emerald-300">
            {counts.valid}
          </dd>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3">
          <dt className="text-xs text-white/60">
            {LL.campaignImport.confirm.timezoneLabel()}
          </dt>
          <dd className="truncate font-semibold">{state.timezone}</dd>
        </div>
      </dl>

      {state.skipInvalid && counts.invalid > 0 ? (
        <p className="text-sm text-white/75" data-slot="skipped-count">
          {LL.campaignImport.review.skippedCount({ count: counts.invalid })}
        </p>
      ) : null}

      <section
        aria-labelledby="import-schedule-title"
        data-slot="schedule-summary"
        className="flex items-start gap-4 rounded-xl border border-[#155DFC]/50 bg-[#155DFC]/15 p-5"
      >
        <CalendarClock
          className="mt-0.5 size-6 shrink-0 text-[#7FA6FF]"
          aria-hidden
        />
        <div className="space-y-1">
          <h3 id="import-schedule-title" className="text-base font-semibold">
            {LL.campaignImport.confirm.scheduleTitle()}
          </h3>
          <p className="max-w-prose text-sm text-white/85">
            {from === to
              ? LL.campaignImport.confirm.scheduleSummarySameDate({
                  count: counts.valid,
                  date: from,
                })
              : LL.campaignImport.confirm.scheduleSummary({
                  count: counts.valid,
                  from,
                  to,
                })}
          </p>
        </div>
      </section>

      <p className="max-w-prose text-sm text-white/65">
        {LL.campaignImport.confirm.draftNote()}
      </p>

      {submission.status === 'failed' ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-100"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>{failures[submission.reason]}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={isSending}
          onClick={() => dispatch({ type: 'goBack' })}
          className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          {LL.campaignImport.confirm.back()}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={isSending}
          onClick={() => onSubmit('draft')}
          className="text-white/80 hover:bg-white/10 hover:text-white"
        >
          {LL.campaignImport.confirm.saveAsDraft()}
        </Button>
        <Button
          type="button"
          disabled={isSending}
          onClick={() => onSubmit('schedule')}
          className="bg-[#155DFC] text-white hover:bg-[#155DFC]/90"
        >
          {isSending ? (
            <>
              <Loader2 className="animate-spin" aria-hidden />
              {LL.campaignImport.confirm.sending()}
            </>
          ) : (
            LL.campaignImport.confirm.submitAndSchedule()
          )}
        </Button>
      </div>
    </div>
  );
}
